<template>
  <!--
    My Decks tab content. Two visible sections: Favorites (decks
    favorited from Browse) and Custom Decks (decks the Mystic built,
    including incomplete ones). When BOTH are empty, render an empty
    state with two big CTAs that switch the parent's mode tab to
    Browse / Build Custom Deck.
  -->
  <div class="my-decks-view">
    <div v-if="loading" class="status">Loading your decks…</div>

    <!-- Empty state — no favorites, custom, or active deck to show -->
    <div
      v-else-if="!favorites.length && !customDecks.length && !activeDecks.length"
      class="empty-state"
    >
      <div class="empty-icon">✦</div>
      <h3>You don't have any decks yet</h3>
      <p>
        Browse the platform pool to favorite a deck, or build your own
        from a brief - it only takes a few minutes.
      </p>
      <div class="cta-row">
        <button class="ghost-btn cta-btn" @click="$emit('go-browse')">
          Browse for a deck
        </button>
        <button class="primary-btn cta-btn" @click="$emit('go-build')">
          Build Custom Deck
        </button>
      </div>
    </div>

    <template v-else>
      <!-- One unified gallery — custom + favorited decks together, each
           tagged Custom / Favorite in the corner. -->
      <div class="gallery">
        <button
          v-for="d in allDecks"
          :key="d._kind + '-' + d.slug"
          type="button"
          class="tile"
          :class="[d._kind === 'custom' ? phaseClass(d) : '', { active: isActiveDeck(d) }]"
          @click="d._kind === 'custom' ? $emit('open-custom', d) : $emit('open-favorite', d)"
        >
          <div class="tile-frame">
            <span v-if="isActiveDeck(d)" class="active-ribbon">Active</span>
            <img v-if="thumbUrl(d)" :src="thumbUrl(d)" :alt="d.name" />
            <div v-else class="tile-placeholder">
              <span>{{ d._kind === 'custom' ? phaseLabel(d.creation_phase) : ' - ' }}</span>
            </div>
            <!-- Phase chip only for in-flight decks; an approved deck is
                 self-evident from "78 / 78 cards" (no "Ready" needed). -->
            <div
              v-if="d._kind === 'custom' && d.creation_phase !== 'approved'"
              class="phase-chip"
              :class="phaseClass(d)"
            >
              {{ phaseLabel(d.creation_phase) }}
            </div>
            <button
              v-if="d._kind === 'custom' && d.creation_phase === 'abandoned'"
              type="button"
              class="delete-btn"
              title="Delete this incomplete deck"
              @click.stop="$emit('delete-custom', d)"
            >×</button>
          </div>
          <div class="tile-name">{{ d.name }}</div>
          <div class="tile-foot">
            <span v-if="d._kind === 'custom'" class="tile-sub">{{ d.cards_generated_count || 0 }} / 78 cards</span>
            <span class="kind-badge" :class="'kind-' + d._kind">
              {{ d._kind === 'custom' ? 'Custom' : 'Favorite' }}
            </span>
          </div>
        </button>
      </div>
      <p v-if="incompleteCount >= 5" class="cap-warning">
        You've hit the 5-incomplete cap. Delete an incomplete deck
        before building a new one.
      </p>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { store } from '@/store'

const props = defineProps({
  favorites: { type: Array, default: () => [] },
  customDecks: { type: Array, default: () => [] },
  // The active deck when it's a platform-pool deck that isn't favorited — it still
  // belongs in My Decks (same shape as favorites).
  activeDecks: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
})
defineEmits([
  'go-browse', 'go-build',
  'open-favorite', 'open-custom',
  'delete-custom',
])

const incompleteCount = computed(
  () => props.customDecks.filter(d => d.creation_phase === 'abandoned').length,
)

// Custom + favorited decks in one list, each tagged with its kind so the
// gallery can render a single grid with a Custom/Favorite badge per tile.
// The active/selected deck always sorts first.
const allDecks = computed(() => {
  const merged = [
    ...props.customDecks.map(d => ({ ...d, _kind: 'custom' })),
    ...props.favorites.map(d => ({ ...d, _kind: 'favorite' })),
    ...props.activeDecks.map(d => ({ ...d, _kind: 'favorite' })),
  ]
  return merged.sort((a, b) => (isActiveDeck(b) ? 1 : 0) - (isActiveDeck(a) ? 1 : 0))
})

// Active = matches any of the operator's variant chosen_deck_template_slugs.
const activeSlugs = computed(() => {
  const s = new Set()
  for (const v of store.readerSkillVariants || []) {
    if (v.is_active === false) continue
    if (v.deck_template_slug) s.add(v.deck_template_slug)
  }
  return s
})
function isActiveDeck(d) { return activeSlugs.value.has(d.slug) }

function thumbUrl(d) {
  return d.back_image_url || d.cover_card_url || ''
}
function phaseClass(d) {
  return `phase-${d.creation_phase || 'unknown'}`
}
function phaseLabel(phase) {
  switch (phase) {
    case 'sample': return 'In progress'
    case 'committed': return 'Generating'
    case 'approved': return 'Ready'
    case 'abandoned': return 'Incomplete'
    default: return phase || ''
  }
}
</script>

<style scoped>
.my-decks-view {
  display: flex;
  flex-direction: column;
  gap: 28px;
}
.section {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.status {
  color: rgba(212, 196, 232, 0.55);
  font-size: 13px;
  font-style: italic;
}

/* ── Empty state ────────────────────────────────────── */
.empty-state {
  text-align: center;
  padding: 56px 24px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px dashed rgba(195, 169, 255, 0.18);
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}
.empty-icon { font-size: 36px; color: rgba(201, 169, 78, 0.55); }
.empty-state h3 {
  font-family: 'Cinzel', serif;
  color: #C9A94E;
  margin: 0;
  font-size: 18px;
}
.empty-state p {
  color: rgba(212, 196, 232, 0.7);
  font-size: 14px;
  max-width: 460px;
  line-height: 1.55;
  margin: 0;
}
.cta-row {
  display: flex;
  gap: 12px;
  margin-top: 8px;
  flex-wrap: wrap;
  justify-content: center;
}
.cta-btn {
  padding: 14px 26px;
  font-size: 14px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}
.primary-btn { background: #663399; color: #fff; border: none; }
.primary-btn:hover { background: #7a3fb1; }
.ghost-btn {
  background: transparent;
  border: 1px solid rgba(195, 169, 255, 0.4);
  color: rgba(212, 196, 232, 0.9);
}
.ghost-btn:hover { border-color: #C9A94E; color: #C9A94E; }

.section-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}
.section-title {
  font-family: 'Cinzel', serif;
  color: #C9A94E;
  font-size: 16px;
  margin: 0;
}
.ghost-btn-sm {
  background: transparent;
  border: 1px dashed rgba(195, 169, 255, 0.32);
  color: rgba(212, 196, 232, 0.85);
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
}
.ghost-btn-sm:hover { border-color: #C9A94E; color: #C9A94E; border-style: solid; }
.incomplete-meter {
  font-size: 11px;
  color: rgba(212, 196, 232, 0.55);
  letter-spacing: 0.04em;
}
.incomplete-meter.full {
  color: rgba(244, 67, 54, 0.9);
  font-weight: 600;
}
.cap-warning {
  color: rgba(244, 67, 54, 0.9);
  font-size: 12px;
  margin: 0;
  font-style: italic;
}

.section-empty {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.section-empty-note {
  color: rgba(212, 196, 232, 0.6);
  font-size: 13px;
  margin: 0;
  font-style: italic;
}

/* ── Tile gallery ───────────────────────────────────── */
.gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
  gap: 18px 14px;
}
.tile {
  position: relative;
  background: transparent;
  border: 1px solid rgba(195, 169, 255, 0.14);
  border-radius: 10px;
  padding: 8px;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  display: flex;
  flex-direction: column;
  gap: 6px;
  transition: all 0.18s;
}
.tile:hover {
  border-color: #C9A94E;
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4),
              0 0 18px rgba(201, 169, 78, 0.14);
}
.tile.phase-approved { border-color: rgba(201, 169, 78, 0.45); }
.tile.phase-abandoned { opacity: 0.55; }
/* The deck in use: gold, like everywhere else the app marks what is current;
   the ribbon sits inside the frame so the row never shifts for it. */
.tile.active {
  border-color: #C9A94E;
  box-shadow: 0 0 0 1px rgba(201, 169, 78, 0.4),
              0 0 22px rgba(201, 169, 78, 0.28),
              0 6px 18px rgba(0, 0, 0, 0.4);
}
.tile.active:hover { border-color: #E0BC60; }
.active-ribbon {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
  font-family: 'Cinzel', serif;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #1a0c2e;
  background: linear-gradient(135deg, #E0BC60 0%, #C9A94E 100%);
  border-radius: 999px;
  padding: 3px 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.35);
  white-space: nowrap;
}

.tile-frame {
  position: relative;
  aspect-ratio: 2 / 3;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 6px;
  overflow: hidden;
}
.tile-frame img {
  width: 100%; height: 100%;
  object-fit: cover; display: block;
}
.tile-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(212, 196, 232, 0.4);
  font-size: 11px;
  font-style: italic;
}
.phase-chip {
  position: absolute;
  top: 6px;
  left: 6px;
  font-family: 'Cinzel', serif;
  font-size: 9px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.7);
  color: rgba(212, 196, 232, 0.85);
  border: 1px solid rgba(195, 169, 255, 0.22);
}
.phase-chip.phase-approved {
  background: rgba(201, 169, 78, 0.15);
  color: #C9A94E;
  border-color: rgba(201, 169, 78, 0.45);
}
.phase-chip.phase-sample,
.phase-chip.phase-committed {
  background: rgba(102, 51, 153, 0.4);
  color: #f3e9d2;
  border-color: rgba(195, 169, 255, 0.45);
}
.phase-chip.phase-abandoned {
  background: rgba(0, 0, 0, 0.55);
  color: rgba(212, 196, 232, 0.55);
}
.delete-btn {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.65);
  border: 1px solid rgba(244, 67, 54, 0.55);
  color: rgba(244, 67, 54, 0.9);
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
}
.delete-btn:hover {
  background: rgba(244, 67, 54, 0.18);
  color: #fff;
}

.tile-name {
  font-family: 'Cinzel', serif;
  color: #C9A94E;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 4px;
}
.tile-foot {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 4px;
}
.tile-sub {
  font-size: 11px;
  color: rgba(212, 196, 232, 0.55);
}
/* Custom / Favorite label, bottom-right of each tile. */
.kind-badge {
  margin-left: auto;
  font-family: 'Cinzel', serif;
  font-size: 9px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid transparent;
  white-space: nowrap;
}
.kind-custom {
  color: #C9A94E;
  border-color: rgba(201, 169, 78, 0.45);
  background: rgba(201, 169, 78, 0.12);
}
.kind-favorite {
  color: #c3a9ff;
  border-color: rgba(195, 169, 255, 0.4);
  background: rgba(195, 169, 255, 0.1);
}
</style>
